import {
    Chip,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useField } from 'formik';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import useCommonStyles from '../../../../useCommonStyles';
import { DealerStatus, SelectedDealer } from './types';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
    },
    inline: {
        display: 'inline',
    },
    inSessionStatus: {
        color: '#1f6b5a',
        backgroundColor: '#e7f1ed',
    },
    newStatus: {
        color: '#856404',
        backgroundColor: '#fbeccb',
    },
}));

type SelectedDealersListProps = {
    displayDealerTagNames?: boolean;
    selectedDealers: SelectedDealer[];
};

const SelectedDealersList = ({ displayDealerTagNames, selectedDealers }: SelectedDealersListProps) => {
    const { t } = useTranslation(['purchaseTransactionsPage', 'common']);
    const [, meta] = useField<string[]>('selectedDealerIds');
    const classes = useStyles();
    const commonStyles = useCommonStyles();
    const hasError = !!meta.error && meta.touched;

    const renderStatus = (status: DealerStatus) => {
        if (status === DealerStatus.InSession) {
            return (
                <Chip
                    className={classes.inSessionStatus}
                    label={t('purchaseTransactionsPage:quotation.inviteQuote.status.inSession')}
                    size="small"
                />
            );
        }

        return (
            <Chip
                className={classes.newStatus}
                label={t('purchaseTransactionsPage:quotation.inviteQuote.status.new')}
                size="small"
            />
        );
    };

    return (
        <FormControl fullWidth>
            <FormLabel className={commonStyles.formLabel}>
                {t('purchaseTransactionsPage:quotation.inviteQuote.selectedDealers')}
            </FormLabel>
            <List className={classes.root}>
                {selectedDealers.length === 0 && (
                    <ListItem>
                        <ListItemText primary={t('purchaseTransactionsPage:quotation.inviteQuote.noDealersSelected')} />
                    </ListItem>
                )}
                {selectedDealers.map((dealer, index) => (
                    <Fragment key={dealer.id}>
                        {index > 0 && <Divider />}
                        <ListItem alignItems="flex-start">
                            <ListItemText
                                primary={dealer.name}
                                secondary={
                                    displayDealerTagNames && dealer.tagNames ? (
                                        <Typography className={classes.inline} color="secondary" component="span">
                                            {dealer.tagNames}
                                        </Typography>
                                    ) : null
                                }
                            />
                            <ListItemSecondaryAction>{renderStatus(dealer.status)}</ListItemSecondaryAction>
                        </ListItem>
                    </Fragment>
                ))}
            </List>
            <FormHelperText error={hasError}>{hasError ? meta.error : ''}</FormHelperText>
        </FormControl>
    );
};

export default SelectedDealersList;
